<template>
  <div>
    <!-- Hero Section -->
    <HeroSlider />

    <!-- Featured Products Section -->
    <section class="featured-section">
      <div class="product-grid-container">
        <header class="section-header">
          <h2 class="section-label">{{ t('product.latest_drops') }}</h2>
          <router-link to="/t-shirt" class="shop-all-link">{{ t('product.view_all') }}</router-link>
        </header>
        
        <div v-if="isLoading" class="grid-status-message">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">{{ t('product.status.loading') }}</span>
          </div>
        </div>
        <div v-else-if="error" class="grid-status-message">
          <ErrorState 
            :message="error || t('product.error.load_failed')" 
            @retry="retryLoadProducts"
          />
        </div>
        <template v-else>
          <div
            v-for="product in products"
            :key="product.id"
            class="product-card-wrapper"
          >
            <ProductCard
              :product="product"
              @view-details="openModal"
            />
          </div>
        </template>
      </div>
    </section>

    <!-- Modal Container -->
    <ProductModalHorizontal
      v-if="selectedProduct"
      :product="selectedProduct"
      @close="selectedProduct = null"
    />
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue'
import { useCatalog } from '../composables/useCatalog'
import HeroSlider from '../components/HeroSlider.vue'
import ProductCard from '../components/ProductCard.vue'
import ProductModalHorizontal from '../components/ProductModalHorizontal.vue'
import ErrorState from '../components/ErrorState.vue'
import { useLanguage } from '../composables/useLanguage'

const { t } = useLanguage()
const { products, isLoading, error, fetchProducts } = useCatalog()
const selectedProduct = ref(null)
const isMobile = ref(false)

const openModal = (product) => {
  if (product && product.isActive) {
    selectedProduct.value = product
  }
}

const checkMobile = () => {
  isMobile.value = window.innerWidth < 768
}

const retryLoadProducts = async () => {
  await fetchProducts({ isNew: true })
}

onMounted(async () => {
  checkMobile()
  window.addEventListener('resize', checkMobile)
  
  await fetchProducts({ isNew: true })
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', checkMobile)
})
</script>

<style scoped>
.featured-section {
  padding: 80px 0 60px 0; /* Aumentato lo spazio superiore per separare dal HeroSlider */
  background-color: #ffffff;
}

.section-header {
  grid-column: 1 / -1;
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  border-bottom: 1px solid #eaeaea;
  padding-bottom: 15px;
  margin-bottom: 1.5rem; /* Margine impostato a 1.5rem come da specifica */
}

.section-label {
  text-align: left;
  font-size: 14px; /* Proporzione aurea: 14px bold */
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 2px;
  color: #000;
  margin-bottom: 0;
  font-family: 'Montserrat', sans-serif;
}

.shop-all-link {
  font-size: 14px; /* Allineato a 14px come richiesto */
  font-weight: 400;
  color: #888;
  text-decoration: none;
  text-transform: uppercase;
  letter-spacing: 1px;
  transition: color 0.3s ease;
}

.shop-all-link:hover {
  color: #000;
}

/* Struttura Fluida Edge-to-Edge per la Griglia Prodotti */
.product-grid-container {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  column-gap: 2.5rem;
  row-gap: 0.5rem;
  padding: 0 6vw;
  width: 100%;
}

.grid-status-message {
  grid-column: 1 / -1;
  text-align: center;
  padding: 60px 0;
}

@media (max-width: 991px) {
  .product-grid-container {
    grid-template-columns: repeat(2, 1fr);
    gap: 1.5rem;
    padding: 0 4vw;
  }
}

@media (max-width: 576px) {
  .product-grid-container {
    grid-template-columns: 1fr;
    padding: 0 20px;
  }
}

/* Effetto Interattivo Premium sulle Immagini dei Capi */
.product-card-wrapper :deep(.image-area) {
  overflow: hidden;
  position: relative;
}

.product-card-wrapper :deep(.block-image) {
  transition: transform 0.8s cubic-bezier(0.25, 1, 0.5, 1), opacity 0.4s ease !important;
}

.product-card-wrapper:hover :deep(.block-image) {
  transform: scale(1.03); /* Zoom fluido impercettibile e di lusso */
}

/* Micro-correzioni Info Prodotto per Bilanciamento Lusso/Leggibilità */
.product-card-wrapper :deep(.item-name) {
  font-size: 16px !important; /* Uniformato a 16px per leggibilità chiara */
  font-weight: 700 !important;
}

.product-card-wrapper :deep(.item-price) {
  font-size: 16px !important; /* Prezzo bilanciato a 16px */
  font-weight: 700 !important;
}

.product-card-wrapper :deep(.item-edition) {
  font-size: 10px !important; /* Leggermente ridotto per gerarchia visiva */
}
</style>
